import React, { useState } from 'react';
import { SCREEN_OCCUPANCY, SCREENS_DATA } from '../data/mockData';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Users, Armchair } from 'lucide-react';
const getOccupancyColor = (occ: number) => {
  if (occ >= 90) return {
    bg: 'bg-red-600',
    text: 'text-white',
    label: 'HOUSEFULL'
  };
  if (occ >= 75) return {
    bg: 'bg-green-600',
    text: 'text-white',
    label: 'High'
  };
  if (occ >= 50) return {
    bg: 'bg-amber-500',
    text: 'text-white',
    label: 'Medium'
  };
  if (occ >= 25) return {
    bg: 'bg-amber-300',
    text: 'text-gray-900',
    label: 'Low'
  };
  return {
    bg: 'bg-gray-200',
    text: 'text-gray-600',
    label: 'Very Low'
  };
};
const getOccupancyBorder = (occ: number) => {
  if (occ >= 90) return 'border-red-700';
  if (occ >= 75) return 'border-green-700';
  if (occ >= 50) return 'border-amber-600';
  if (occ >= 25) return 'border-amber-400';
  return 'border-gray-300';
};
export function SeatOccupancy() {
  const [selectedScreen, setSelectedScreen] = useState<number | null>(null);
  // Calculate summary stats
  const allShows = SCREEN_OCCUPANCY.flatMap(s => s.shows);
  const avgOccupancy = Math.round(allShows.reduce((sum, s) => sum + s.occupancy, 0) / allShows.length);
  const housefullCount = allShows.filter(s => s.occupancy >= 95).length;
  const totalShows = allShows.length;
  const lowOccupancy = allShows.filter(s => s.occupancy < 50).length;
  const filteredScreens = selectedScreen ? SCREEN_OCCUPANCY.filter(s => s.id === selectedScreen) : SCREEN_OCCUPANCY;
  return <div className="space-y-6" data-id="element-588">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-589">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.05
      }} className="bg-white rounded-xl p-4 border border-gray-100 soft-shadow" data-id="element-590">
          <div className="flex items-center gap-3" data-id="element-591">
            <div className="p-2 rounded-lg bg-green-50" data-id="element-592">
              <TrendingUp className="w-4 h-4 text-green-600" data-id="element-593" />
            </div>
            <div data-id="element-594">
              <p className="text-xs text-gray-500 font-medium" data-id="element-595">Avg Occupancy</p>
              <p className="text-xl font-bold text-gray-900" data-id="element-596">{avgOccupancy}%</p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white rounded-xl p-4 border border-gray-100 soft-shadow" data-id="element-597">
          <div className="flex items-center gap-3" data-id="element-598">
            <div className="p-2 rounded-lg" style={{
            backgroundColor: 'color-mix(in srgb, var(--accent-color) 10%, white)'
          }} data-id="element-599">
              <Users className="w-4 h-4" style={{
              color: 'var(--accent-color)'
            }} data-id="element-600" />
            </div>
            <div data-id="element-601">
              <p className="text-xs text-gray-500 font-medium" data-id="element-602">
                Housefull Shows
              </p>
              <p className="text-xl font-bold text-gray-900" data-id="element-603">
                {housefullCount}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15
      }} className="bg-white rounded-xl p-4 border border-gray-100 soft-shadow" data-id="element-604">
          <div className="flex items-center gap-3" data-id="element-605">
            <div className="p-2 rounded-lg bg-blue-50" data-id="element-606">
              <Armchair className="w-4 h-4 text-blue-600" data-id="element-607" />
            </div>
            <div data-id="element-608">
              <p className="text-xs text-gray-500 font-medium" data-id="element-609">Total Shows</p>
              <p className="text-xl font-bold text-gray-900" data-id="element-610">{totalShows}</p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-white rounded-xl p-4 border border-gray-100 soft-shadow" data-id="element-611">
          <div className="flex items-center gap-3" data-id="element-612">
            <div className="p-2 rounded-lg bg-amber-50" data-id="element-613">
              <TrendingDown className="w-4 h-4 text-amber-600" data-id="element-614" />
            </div>
            <div data-id="element-615">
              <p className="text-xs text-gray-500 font-medium" data-id="element-616">Low Occupancy</p>
              <p className="text-xl font-bold text-gray-900" data-id="element-617">{lowOccupancy}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Screen Filter */}
      <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar pb-1" data-id="element-618">
        <button onClick={() => setSelectedScreen(null)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${selectedScreen === null ? 'text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} style={selectedScreen === null ? {
        backgroundColor: 'var(--accent-color)'
      } : {}} data-id="element-619">
          All Screens
        </button>
        {SCREENS_DATA.filter(s => s.status === 'ACTIVE').map(screen => <button key={screen.id} onClick={() => setSelectedScreen(screen.id === selectedScreen ? null : screen.id)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${selectedScreen === screen.id ? 'text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} style={selectedScreen === screen.id ? {
        backgroundColor: 'var(--accent-color)'
      } : {}} data-id="element-620">
            {screen.name}
            <span className="ml-1.5 text-[10px] opacity-75" data-id="element-621">{screen.type}</span>
          </button>)}
      </div>

      {/* Occupancy Heatmap Grid */}
      <div className="space-y-4" data-id="element-622">
        {filteredScreens.map((screen, screenIdx) => {
        const screenInfo = SCREENS_DATA.find(s => s.id === screen.id);
        const screenAvg = Math.round(screen.shows.reduce((sum, s) => sum + s.occupancy, 0) / screen.shows.length);
        return <motion.div key={screen.id} initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.05 * screenIdx
        }} className="bg-white rounded-xl border border-gray-100 soft-shadow overflow-hidden" data-id="element-623">
              {/* Screen Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-100" data-id="element-624">
                <div className="flex items-center gap-3" data-id="element-625">
                  <h3 className="font-bold text-gray-900" data-id="element-626">{screen.name}</h3>
                  {screenInfo && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600" data-id="element-627">
                      {screenInfo.type}
                    </span>}
                  {screenInfo && <span className="text-xs text-gray-400" data-id="element-628">
                      {screenInfo.capacity} seats
                    </span>}
                </div>
                <div className="flex items-center gap-2" data-id="element-629">
                  <span className="text-xs text-gray-500" data-id="element-630">Avg:</span>
                  <span className={`text-sm font-bold ${screenAvg >= 75 ? 'text-green-600' : screenAvg >= 50 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-631">
                    {screenAvg}%
                  </span>
                </div>
              </div>

              {/* Shows Grid */}
              <div className="p-4" data-id="element-632">
                <div className="flex flex-wrap gap-3" data-id="element-633">
                  {screen.shows.map((show, showIdx) => {
                const colors = getOccupancyColor(show.occupancy);
                const borderColor = getOccupancyBorder(show.occupancy);
                return <div key={showIdx} className={`relative rounded-lg border-2 ${borderColor} overflow-hidden min-w-[140px] flex-1 max-w-[200px]`} data-id="element-634">
                        {/* Occupancy fill bar */}
                        <div className={`absolute inset-0 ${colors.bg} opacity-15`} style={{
                    width: `${show.occupancy}%`
                  }} data-id="element-635" />

                        <div className="relative p-3" data-id="element-636">
                          <div className="flex items-center justify-between mb-1.5" data-id="element-637">
                            <span className="text-xs font-bold text-gray-900" data-id="element-638">
                              {show.time}
                            </span>
                            {show.occupancy >= 95 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded text-white" style={{
                        backgroundColor: 'var(--accent-color)'
                      }} data-id="element-639">
                                FULL
                              </span>}
                          </div>
                          <p className="text-xs font-medium text-gray-700 truncate mb-2" data-id="element-640">
                            {show.film}
                          </p>
                          <div className="flex items-center gap-2" data-id="element-641">
                            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-642">
                              <div className={`h-full rounded-full transition-all ${colors.bg}`} style={{
                          width: `${show.occupancy}%`
                        }} data-id="element-643" />
                            </div>
                            <span className="text-xs font-bold text-gray-900 tabular-nums w-8 text-right" data-id="element-644">
                              {show.occupancy}%
                            </span>
                          </div>
                        </div>
                      </div>;
              })}
                </div>
              </div>
            </motion.div>;
      })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 py-4" data-id="element-645">
        <div className="flex items-center gap-2" data-id="element-646">
          <div className="w-3 h-3 rounded-sm bg-red-600" data-id="element-647" />
          <span className="text-xs text-gray-500" data-id="element-648">90%+ Housefull</span>
        </div>
        <div className="flex items-center gap-2" data-id="element-649">
          <div className="w-3 h-3 rounded-sm bg-green-600" data-id="element-650" />
          <span className="text-xs text-gray-500" data-id="element-651">75–89% High</span>
        </div>
        <div className="flex items-center gap-2" data-id="element-652">
          <div className="w-3 h-3 rounded-sm bg-amber-500" data-id="element-653" />
          <span className="text-xs text-gray-500" data-id="element-654">50–74% Medium</span>
        </div>
        <div className="flex items-center gap-2" data-id="element-655">
          <div className="w-3 h-3 rounded-sm bg-gray-200" data-id="element-656" />
          <span className="text-xs text-gray-500" data-id="element-657">&lt;50% Low</span>
        </div>
      </div>
    </div>;
}